import { useQuery } from "@tanstack/react-query";

import { getTrainingMeStatus, type TrainingMeStatusItem } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { DEMO_MOCK } from "@/lib/demo-flags";

export interface TrainingLockout {
  /** True when the current user is a channel_partner with outstanding
   * compulsory training and must not create/register deals. Mirrors the
   * backend `require_training_complete` dep — any channel_partner (BDM or PO)
   * is gated (CHP-1047). */
  blocked: boolean;
  /** Count of outstanding compulsory modules (drives the explanatory copy). */
  outstandingCount: number;
  /** Per-module status list (deals-gap W2) — feeds the differentiated
   * never-started vs partially-complete block screens. */
  statuses: TrainingMeStatusItem[];
  isLoading: boolean;
}

/**
 * Frontend half of the RACCPE-22 compulsory-training lockout. The backend dep
 * is authoritative; this is UX so a blocked BDM sees an explanatory state
 * instead of clicking into a wizard that would 403 on save.
 *
 * Shares the `["training", "me-status"]` cache entry with the partner
 * dashboard's outstanding-training card, so no extra request is issued.
 */
export function useTrainingLockout(options?: { enabled?: boolean }): TrainingLockout {
  const { accessToken, data: user } = useCurrentUser();
  // CHP-809 (#26): callers in a context where the lockout is irrelevant (e.g.
  // the admin training preview, which never reaches the completion branch that
  // reads it) can pass `enabled: false` to skip the /training/me/status fetch.
  const enabled = options?.enabled ?? true;

  const query = useQuery({
    queryKey: ["training", "me-status"],
    queryFn: () => getTrainingMeStatus(accessToken!),
    enabled: enabled && !DEMO_MOCK && !!accessToken,
  });

  const statuses = query.data ?? [];
  const outstandingCount = statuses.filter((s) => s.outstanding).length;
  // CHP-1047: every channel_partner is gate-eligible — BDMs, POs, and
  // profileless partners alike. This mirrors the backend `require_training_complete`
  // dep, which dropped its PO (`is_org_admin`) exemption. Non-partner roles
  // (CM/RM/RA) are never blocked, matching the backend's early return. An active
  // RM/RA deal-block exemption (RACCPE-23) also lifts the block.
  const isGateEligiblePartner = user?.role === "channel_partner";
  const exempted = Boolean(user?.training_deal_block_exempted);
  const blocked = isGateEligiblePartner && !exempted && outstandingCount > 0;

  return { blocked, outstandingCount, statuses, isLoading: query.isLoading };
}
